import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import apiClient from '../utils/apiClient';
import {
  TrashIcon,
  ExclamationTriangleIcon,
  UserIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import ConfirmationModal from './ConfirmationModal';

interface Member {
  mg_nr?: string;
  name: string;
  email: string;
}

interface DeleteEventModalProps {
  event: any;
  onDelete: (response: any) => void;
  onClose: () => void;
  user: any;
  isPastEvent: boolean;
  isOpen: boolean;
}

const DeleteEventModal: React.FC<DeleteEventModalProps> = ({
  event,
  onDelete,
  onClose,
  user,
  isPastEvent,
  isOpen
}) => {
  const { config } = useContext(AuthContext);
  const BASE_URL = process.env.REACT_APP_API_URL || config?.apiUrl || 'http://localhost:4000';

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDeleteOptionModal, setShowDeleteOptionModal] = useState(false);
  const [deleteMode, setDeleteMode] = useState<'single' | 'future'>('single');
  const [relatedEventsCount, setRelatedEventsCount] = useState(0);
  const [affectedMembers, setAffectedMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState({ delete: false, checking: false });
  const [error, setError] = useState<string | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [confirmChecked, setConfirmChecked] = useState(false);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const checkRelatedEvents = async () => {
    if (!event) return 0;
    try {
      const response = await apiClient.get(`/api/calendar/related-events/${event.id}`);
      if (response.data.success) {
        setRelatedEventsCount(response.data.count);
        return response.data.count;
      }
      return 0;
    } catch (error: any) {
      console.error('Error checking related events:', error);
      return 0;
    }
  };

  const fetchAffectedMembersForSingle = async (eventId: string) => {
    try {
      const response = await apiClient.get(`/api/calendar/pre-delete/${eventId}?allFuture=false`);
      if (response.data.success) {
        return {
          members: response.data.members || [],
          count: response.data.count || 0,
        };
      }
      return { members: [], count: 0 };
    } catch (error) {
      console.error('Error fetching affected members:', error);
      return { members: [], count: 0 };
    }
  };

  const fetchAffectedMembersForSeries = async (eventId: string) => {
    try {
      const response = await apiClient.get(`/api/calendar/pre-delete/${eventId}?allFuture=true`);
      if (response.data.success) {
        return {
          members: response.data.members || [],
          count: response.data.count || 0,
        };
      }
      return { members: [], count: 0 };
    } catch (error) {
      console.error('Error fetching affected members:', error);
      return { members: [], count: 0 };
    }
  };

  const handleDeleteClick = async () => {
    setLoading(prev => ({ ...prev, checking: true }));
    try {
      const count = await checkRelatedEvents();

      if (count > 1) {
        setShowDeleteOptionModal(true);
      } else {
        const { members } = await fetchAffectedMembersForSingle(event.id);
        setAffectedMembers(members);
        setDeleteMode('single');
        setShowDeleteConfirm(true);
      }
    } catch (error: any) {
      setError(`Fehler: ${error.message}`);
      setAffectedMembers([]);
      setDeleteMode('single');
      setShowDeleteConfirm(true);
    } finally {
      setLoading(prev => ({ ...prev, checking: false }));
    }
  };

  const handleDeleteOptionConfirm = async (allFuture: boolean) => {
    setDeleteMode(allFuture ? 'future' : 'single');
    setShowDeleteOptionModal(false);
    try {
      const { members, count } = allFuture
        ? await fetchAffectedMembersForSeries(event.id)
        : await fetchAffectedMembersForSingle(event.id);
      setRelatedEventsCount(count);
      setAffectedMembers(members);
      setShowDeleteConfirm(true);
    } catch (error: any) {
      setError(`Fehler: ${error.message}`);
    }
  };

  const handleDeleteConfirm = async () => {
    setLoading(prev => ({ ...prev, delete: true }));
    try {
      const endpoint = deleteMode === 'future'
        ? `/api/calendar/delete-future/${event.id}`
        : `/api/calendar/${event.id}`;

      const response = await apiClient.delete(endpoint);

      if (response.data.success) {
        setShowDeleteConfirm(false);
        onDelete(response.data);
        onClose();
      } else {
        setError(response.data.message || 'Loeschen fehlgeschlagen');
      }
    } catch (error: any) {
      setError(`Fehler beim Loeschen: ${error.message}`);
    } finally {
      setLoading(prev => ({ ...prev, delete: false }));
    }
  };

  useEffect(() => {
    if (isOpen && event) {
      setShowDeleteConfirm(false);
      setShowDeleteOptionModal(false);
      setError(null);
      setAffectedMembers([]);
      setRelatedEventsCount(0);
      setDeleteMode('single');
      setConfirmChecked(false);
      setIsVisible(false);
      handleDeleteClick();
    }
  }, [isOpen, event?.id]);

  useEffect(() => {
    if (showDeleteConfirm) {
      setTimeout(() => setIsVisible(true), 10);
    } else {
      setIsVisible(false);
    }
  }, [showDeleteConfirm]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <>
      {/* Loading Overlay */}
      {loading.checking && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[70]">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 max-w-xs w-full mx-4 shadow-xl border border-gray-200 dark:border-gray-700">
            <div className="flex flex-col items-center">
              <div className="animate-spin h-12 w-12 border-4 border-error-600 border-t-transparent rounded-full mb-4" />
              <p className="text-lg font-semibold text-gray-900 dark:text-white">Loeschoptionen werden geladen...</p>
            </div>
          </div>
        </div>
      )}

      {/* Delete Options Modal */}
      <ConfirmationModal
        visible={showDeleteOptionModal}
        type="question"
        title="Loeschoptionen"
        message={`Dieses Event ist Teil einer Serie mit ${relatedEventsCount} Terminen. Was moechtest du loeschen?`}
        variant="two"
        buttons={{
          cancel: {
            text: 'Nur dieses Event',
            onPress: () => handleDeleteOptionConfirm(false)
          },
          confirm: {
            text: 'Alle zukuenftigen',
            onPress: () => handleDeleteOptionConfirm(true),
            loading: loading.delete
          }
        }}
        showCloseButton={true}
        onClose={() => {
          setShowDeleteOptionModal(false);
          onClose();
        }}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4"
          onClick={() => {
            setShowDeleteConfirm(false);
            onClose();
          }}
        >
          <div
            className="w-full sm:max-w-lg bg-white dark:bg-[#1c2432] border-t sm:border border-gray-200 dark:border-gray-700 rounded-t-2xl sm:rounded-2xl max-h-[90vh] flex flex-col shadow-2xl animate-slide-up sm:animate-fade-in"
            onClick={(e) => e.stopPropagation()}
          >
              {/* Header */}
              <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-200 dark:border-gray-700">
                <div className="w-9 h-9 rounded-xl bg-error-100 dark:bg-error-900/40 text-error-600 dark:text-error-400 grid place-items-center flex-shrink-0">
                  <TrashIcon className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-bold text-gray-900 dark:text-white truncate">Termin loeschen</h3>
                  <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">{event?.title}</p>
                </div>
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    onClose();
                  }}
                  className="w-9 h-9 grid place-items-center rounded-lg hover:bg-gray-100 dark:hover:bg-white/5 text-gray-500 dark:text-gray-400 flex-shrink-0"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">

                {/* Warning Section */}
                <div className="space-y-3">
                  <h4 className="text-xs font-semibold text-error-600 dark:text-error-400 uppercase tracking-wider flex items-center gap-1.5">
                    <ExclamationTriangleIcon className="w-3.5 h-3.5" />
                    Warnung
                  </h4>
                  <div className="p-4 bg-error-50 dark:bg-error-900/20 rounded-xl border border-error-200 dark:border-error-700">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-error-100 dark:bg-error-900/40 flex items-center justify-center flex-shrink-0">
                        <ExclamationTriangleIcon className="w-5 h-5 text-error-600 dark:text-error-400" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-error-800 dark:text-error-300">
                          {deleteMode === 'future' && relatedEventsCount > 1
                            ? `${relatedEventsCount} zukuenftige Events werden geloescht`
                            : affectedMembers.length > 0
                              ? `${affectedMembers.length} Buchungen betroffen`
                              : 'Dieses Event wird unwiderruflich geloescht'
                          }
                        </p>
                        <p className="text-sm text-error-700 dark:text-error-400 mt-1">
                          {affectedMembers.length > 0
                            ? 'Alle Teilnehmer werden automatisch benachrichtigt und ihre Buchungen storniert.'
                            : 'Diese Aktion kann nicht rueckgaengig gemacht werden.'
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Error Message */}
                {error && (
                  <div className="p-3 bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-700 text-error-700 dark:text-error-300 rounded-xl text-sm flex items-center gap-2">
                    <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0" />
                    {error}
                  </div>
                )}

                {/* Affected Members */}
                {affectedMembers.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                      <UserIcon className="w-3.5 h-3.5" />
                      Betroffene Mitglieder ({affectedMembers.length})
                    </h4>
                    <div className="max-h-48 overflow-y-auto space-y-2">
                      {affectedMembers.map((member, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-200 dark:border-gray-600"
                        >
                          {member.mg_nr ? (
                            <img
                              src={`${BASE_URL}/api/members/user-image-url/${member.mg_nr}?size=S&token=${localStorage.getItem('jwt_token')}`}
                              alt={member.name}
                              className="w-10 h-10 rounded-full object-cover border border-gray-200 dark:border-gray-600"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                                const fallback = target.nextElementSibling as HTMLElement;
                                if (fallback) fallback.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div
                            className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/50 flex items-center justify-center"
                            style={{ display: member.mg_nr ? 'none' : 'flex' }}
                          >
                            <UserIcon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{member.name}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{member.email}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Confirmation Checkbox */}
                <div className="space-y-3">
                  <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                    Bestaetigung
                  </h4>
                  <label className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-200 dark:border-gray-600 cursor-pointer hover:border-error-300 dark:hover:border-error-600 transition-colors">
                    <input
                      type="checkbox"
                      checked={confirmChecked}
                      onChange={(e) => setConfirmChecked(e.target.checked)}
                      className="mt-0.5 w-4 h-4 text-error-600 border-gray-300 dark:border-gray-600 rounded focus:ring-error-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      Ich verstehe, dass diese Aktion nicht rueckgaengig gemacht werden kann.
                    </span>
                  </label>
                </div>
              </div>

              {/* Footer */}
              <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end gap-2 flex-wrap">
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    onClose();
                  }}
                  disabled={loading.delete}
                  className="px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 font-semibold text-sm rounded-lg transition-colors disabled:opacity-50"
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  disabled={loading.delete || !confirmChecked}
                  className="px-4 py-2 bg-error-600 hover:bg-error-700 text-white font-semibold text-sm rounded-lg transition-all disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:text-gray-500 dark:disabled:text-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading.delete ? (
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                  ) : (
                    'Endgueltig loeschen'
                  )}
                </button>
              </div>
            </div>
          </div>
      )}

      {/* Loading Animation while deleting */}
      {loading.delete && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[80]">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 max-w-md w-full mx-4 shadow-xl border border-gray-200 dark:border-gray-700">
            <div className="flex flex-col items-center space-y-6">
              <div className="relative w-24 h-24">
                <div className="absolute inset-0 border-4 border-gray-200 dark:border-gray-700 rounded-full"></div>
                <div className="absolute inset-0 border-4 border-transparent border-t-error-500 rounded-full animate-spin"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <TrashIcon className="w-10 h-10 text-error-500 animate-pulse" />
                </div>
              </div>

              <div className="text-center space-y-2">
                <p className="text-xl font-bold text-gray-900 dark:text-white">
                  {deleteMode === 'future' && relatedEventsCount > 1
                    ? `${relatedEventsCount} Events werden geloescht...`
                    : 'Event wird geloescht...'
                  }
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">Bitte warten</p>
              </div>

              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
                <div className="h-full bg-error-500 rounded-full animate-pulse" style={{ width: '75%' }}></div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DeleteEventModal;
